import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortalContext } from '../../contexts/PortalContext';
import PortalLayout from '../../components/portal/PortalLayout';
import portalApi from '../../services/portalApi';
import { CheckCircle } from 'lucide-react';

const PortalRequestQuote = () => {
  const navigate = useNavigate();
  const { customer, token, isAuthenticated } = usePortalContext();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    description: '',
    address: '',
    city: '',
    state: '',
    zipCode: ''
  });

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/');
      return;
    }

    // Pre-fill with customer address if available
    if (customer) {
      setFormData(prev => ({
        ...prev,
        address: customer.address || '',
        city: customer.city || '',
        state: customer.state || '',
        zipCode: customer.zipCode || ''
      }));
    }
  }, [customer]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      await portalApi.requestQuote(token, formData);
      
      setSuccess(true);
      
      // Reset form
      setFormData({
        description: '',
        address: customer?.address || '',
        city: customer?.city || '',
        state: customer?.state || '',
        zipCode: customer?.zipCode || ''
      });
      
      // Redirect after 3 seconds
      setTimeout(() => {
        navigate('/portal/jobs');
      }, 3000);
    } catch (error) {
      console.error('Error submitting quote request:', error);
      alert('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <PortalLayout>
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Request Submitted!
            </h2>
            <p className="text-gray-600">
              We've received your service request and will get back to you shortly with a quote.
            </p>
            <p className="text-sm text-gray-500 mt-4">
              Redirecting you to your jobs...
            </p>
          </div>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Request a Quote
          </h2>
          <p className="text-gray-600 mb-6">
            Tell us about the service you need and we'll get back to you with a quote.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Description */}
            <div>
              <label className="label">Service Description *</label>
              <textarea
                required
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input"
                rows="4"
                placeholder="Please describe the service you need in detail..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Include any specific requirements, preferred dates, or special requests
              </p>
            </div>

            {/* Address */}
            <div>
              <label className="label">Service Address</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="input"
                placeholder="123 Main Street"
              />
            </div>

            {/* City, State, Zip */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="label">City</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="input"
                  placeholder="City"
                />
              </div>
              <div>
                <label className="label">State</label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className="input"
                  placeholder="NC"
                  maxLength="2"
                />
              </div>
              <div>
                <label className="label">Zip Code</label>
                <input
                  type="text"
                  value={formData.zipCode}
                  onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                  className="input"
                  placeholder="28117"
                />
              </div>
            </div>

            {/* Submit */}
<div className="flex gap-3 pt-4">
  <button
    type="button"
    onClick={() => navigate('/portal/dashboard')}
    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
  >
    Cancel
  </button>
  <button
    type="submit"
    disabled={loading}
    className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl"
  >
    {loading ? 'Submitting...' : 'Submit Request'}
  </button>
</div>
          </form>
        </div>
      </div>
    </PortalLayout>
  );
};

export default PortalRequestQuote;